import { forwardRef, useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import type { CommonProps } from '../@types/common'
import type {
    InputHTMLAttributes,
    ElementType,
    ReactNode,
    HTMLInputTypeAttribute,
} from 'react'

export interface InputProps
    extends CommonProps,
        Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
    asElement?: ElementType
    disabled?: boolean
    invalid?: boolean
    prefix?: string | ReactNode
    size?: 'lg' | 'md' | 'sm' | 'xs'
    suffix?: string | ReactNode
    type?: HTMLInputTypeAttribute
    unstyle?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    (props, ref) => {
        const {
            asElement: Component = 'input',
            className,
            disabled,
            invalid,
            prefix,
            size = 'md',
            suffix,
            type = 'text',
            style,
            unstyle = false,
            ...rest
        } = props

        const [prefixGutter, setPrefixGutter] = useState(0)
        const [suffixGutter, setSuffixGutter] = useState(0)

        const prefixNode = useRef<HTMLDivElement>(null)
        const suffixNode = useRef<HTMLDivElement>(null)

        const getAffixSize = () => {
            if (!prefixNode.current && !suffixNode.current) {
                return
            }
            const prefixNodeWidth = prefixNode?.current?.offsetWidth
            const suffixNodeWidth = suffixNode?.current?.offsetWidth

            if (prefixNodeWidth) {
                setPrefixGutter(prefixNodeWidth)
            }

            if (suffixNodeWidth) {
                setSuffixGutter(suffixNodeWidth)
            }
        }

        useEffect(() => {
            getAffixSize()
        }, [prefix, suffix])

        const remToPxConvertion = (pixel: number) => 0.0625 * pixel

        const affixGutterStyle = () => {
            const leftGutter = `${remToPxConvertion(prefixGutter) + 1}rem`
            const rightGutter = `${remToPxConvertion(suffixGutter) + 1}rem`
            const gutterStyle: {
                paddingLeft?: string
                paddingRight?: string
            } = {}

            if (prefix) {
                gutterStyle.paddingLeft = leftGutter
            }

            if (suffix) {
                gutterStyle.paddingRight = rightGutter
            }

            return gutterStyle
        }

        const inputClass = classNames(
            !unstyle && 'input',
            size && `input-${size}`,
            !unstyle && invalid && 'input-invalid',
            !prefix && !suffix ? className : '',
            disabled && 'input-disabled'
        )

        const inputProps = {
            className: inputClass,
            disabled,
            type,
            ref,
            ...rest,
        }

        if (prefix || suffix) {
            return (
                <span className={classNames('input-wrapper', className)}>
                    {prefix ? (
                        <div ref={prefixNode} className="input-suffix-start">
                            {prefix}
                        </div>
                    ) : null}
                    <Component
                        style={{ ...affixGutterStyle(), ...style }}
                        {...inputProps}
                    />
                    {suffix ? (
                        <div ref={suffixNode} className="input-suffix-end">
                            {suffix}
                        </div>
                    ) : null}
                </span>
            );
        }

        return (
            <Component
                style={{ ...affixGutterStyle(), ...style }}
                {...inputProps}
            />
        );
    }
)

Input.displayName = 'Input'

export default Input 